/**
 * uiSfx — procedural WebAudio menu blips (nav / confirm / back / deny).
 * No audio assets: every sound is a couple of oscillator envelopes built in
 * code. Master toggle + volume persist to localStorage (same key style as
 * AudioSystem's "neon-arena.*" keys).
 *
 * Autoplay policy: the AudioContext is created lazily on the first play()
 * (or explicit unlock()) — in practice always inside a user-gesture handler,
 * so the shell is silent until the player actually presses something.
 *
 * Storage is injectable so persistence is unit-testable in the node
 * environment (no localStorage there).
 */

export type UiSfxName = "nav" | "confirm" | "back" | "deny";

export interface UiSfxStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const UI_SFX_MUTED_KEY = "neon-arena.ui-sfx-muted";
export const UI_SFX_VOLUME_KEY = "neon-arena.ui-sfx-volume";

const DEFAULT_VOLUME = 0.5;

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function defaultStorage(): UiSfxStorage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null; // storage access can throw (privacy modes)
  }
}

type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  const g = globalThis as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

interface BlipSpec {
  type: OscillatorType;
  from: number;
  to?: number;
  dur: number;
  gain: number;
  at?: number;
}

export class UiSfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private _enabled = true;
  private _volume = DEFAULT_VOLUME;

  constructor(private readonly storage: UiSfxStorage | null = defaultStorage()) {
    if (this.storage) {
      try {
        const muted = this.storage.getItem(UI_SFX_MUTED_KEY);
        if (muted !== null) this._enabled = muted !== "true";
        const vol = this.storage.getItem(UI_SFX_VOLUME_KEY);
        if (vol !== null) {
          const f = parseFloat(vol);
          if (Number.isFinite(f)) this._volume = clamp01(f);
        }
      } catch {
        /* unreadable storage — keep defaults */
      }
    }
  }

  get enabled(): boolean {
    return this._enabled;
  }

  setEnabled(on: boolean): void {
    this._enabled = on;
    this.persist(UI_SFX_MUTED_KEY, String(!on));
  }

  /** Flip the master toggle; returns the new enabled state. */
  toggle(): boolean {
    this.setEnabled(!this._enabled);
    return this._enabled;
  }

  get volume(): number {
    return this._volume;
  }

  setVolume(v: number): void {
    this._volume = clamp01(v);
    this.persist(UI_SFX_VOLUME_KEY, String(this._volume));
    if (this.master) this.master.gain.value = this._volume;
  }

  /** Warm the AudioContext from a user gesture (optional — play() also does this). */
  unlock(): void {
    const ctx = this.ensureCtx();
    if (ctx && ctx.state === "suspended") void ctx.resume().catch(() => {});
  }

  play(name: UiSfxName): void {
    if (!this._enabled) return;
    const ctx = this.ensureCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      // Called from a gesture this resolves immediately; the current blip is
      // dropped rather than queued (a late menu blip is worse than none).
      void ctx.resume().catch(() => {});
      if ((ctx.state as string) === "suspended") return;
    }
    try {
      switch (name) {
        case "nav":
          this.blip({ type: "square", from: 760, to: 1040, dur: 0.05, gain: 0.16 });
          break;
        case "confirm":
          this.blip({ type: "triangle", from: 620, dur: 0.06, gain: 0.24 });
          this.blip({ type: "triangle", from: 930, dur: 0.1, gain: 0.24, at: 0.055 });
          break;
        case "back":
          this.blip({ type: "square", from: 640, to: 300, dur: 0.09, gain: 0.16 });
          break;
        case "deny":
          this.blip({ type: "sawtooth", from: 150, to: 116, dur: 0.16, gain: 0.2 });
          this.blip({ type: "sawtooth", from: 157, to: 121, dur: 0.16, gain: 0.14 });
          break;
      }
    } catch {
      /* an audio failure must never break the menu */
    }
  }

  private ensureCtx(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor = audioContextCtor();
    if (!Ctor) return null;
    try {
      this.ctx = new Ctor();
    } catch {
      return null;
    }
    this.master = this.ctx.createGain();
    this.master.gain.value = this._volume;
    this.master.connect(this.ctx.destination);
    return this.ctx;
  }

  private blip(spec: BlipSpec): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t0 = ctx.currentTime + (spec.at ?? 0);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = spec.type;
    osc.frequency.setValueAtTime(spec.from, t0);
    if (spec.to !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, spec.to), t0 + spec.dur);
    }
    gain.gain.setValueAtTime(spec.gain, t0);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + spec.dur);
    osc.connect(gain);
    gain.connect(master);
    osc.start(t0);
    osc.stop(t0 + spec.dur + 0.02);
    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
    };
  }

  private persist(key: string, value: string): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(key, value);
    } catch {
      /* quota / privacy mode — non-fatal */
    }
  }
}

/** App-wide singleton (browser). Tests construct their own UiSfx with fake storage. */
export const uiSfx = new UiSfx();
